import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  AlertCircle,
  Info,
  XCircle,
  Check,
  Eye,
  X,
  RefreshCw,
  TrendingDown,
  Bot,
  ShoppingCart,
  Loader2,
} from 'lucide-react'
import { seoAnalytics } from '@/lib/api'
import { cn } from '@/lib/utils'

const SEVERITY_CONFIG = {
  info: {
    icon: Info,
    color: 'text-blue-600 dark:text-blue-400',
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    border: 'border-blue-200 dark:border-blue-800',
  },
  warning: {
    icon: AlertTriangle,
    color: 'text-amber-600 dark:text-amber-400',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
  },
  error: {
    icon: AlertCircle,
    color: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-50 dark:bg-red-900/20',
    border: 'border-red-200 dark:border-red-800',
  },
  critical: {
    icon: XCircle,
    color: 'text-red-700 dark:text-red-300',
    bg: 'bg-red-100 dark:bg-red-900/30',
    border: 'border-red-300 dark:border-red-700',
  },
}

const ALERT_TYPE_ICONS: Record<string, any> = {
  traffic_drop: TrendingDown,
  bot_missing: Bot,
  crawl_errors: AlertCircle,
  conversion_drop: ShoppingCart,
  position_drop: TrendingDown,
}

interface SeoAlertsListProps {
  onRunChecks?: () => void
}

export default function SeoAlertsList({ onRunChecks }: SeoAlertsListProps) {
  const [filter, setFilter] = useState<'all' | 'active' | 'acknowledged' | 'resolved'>('active')
  const queryClient = useQueryClient()

  const alertsQuery = useQuery({
    queryKey: ['seo-alerts', filter],
    queryFn: () => seoAnalytics.getAlerts(filter === 'all' ? undefined : filter, undefined, 50),
  })

  const countQuery = useQuery({
    queryKey: ['seo-alerts-count'],
    queryFn: () => seoAnalytics.getAlertCount(),
    refetchInterval: 60000, // Every minute
  })

  const acknowledgeMutation = useMutation({
    mutationFn: (alertId: number) => seoAnalytics.acknowledgeAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seo-alerts'] })
      queryClient.invalidateQueries({ queryKey: ['seo-alerts-count'] })
    },
  })

  const resolveMutation = useMutation({
    mutationFn: (alertId: number) => seoAnalytics.resolveAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seo-alerts'] })
      queryClient.invalidateQueries({ queryKey: ['seo-alerts-count'] })
    },
  })

  const dismissMutation = useMutation({
    mutationFn: (alertId: number) => seoAnalytics.dismissAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seo-alerts'] })
      queryClient.invalidateQueries({ queryKey: ['seo-alerts-count'] })
    },
  })

  const runChecksMutation = useMutation({
    mutationFn: () => seoAnalytics.runAlertChecks(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['seo-alerts'] })
      queryClient.invalidateQueries({ queryKey: ['seo-alerts-count'] })
      onRunChecks?.()
    },
  })

  const alerts = alertsQuery.data?.data || []
  const count = countQuery.data?.data || { total: 0, critical: 0, error: 0, warning: 0, info: 0 }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  return (
    <div className="space-y-4">
      {/* Summary and actions */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          {/* Alert counts by severity */}
          <div className="flex items-center gap-2">
            {count.critical > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                {count.critical} criticas
              </span>
            )}
            {count.error > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400">
                {count.error} errores
              </span>
            )}
            {count.warning > 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400">
                {count.warning} avisos
              </span>
            )}
            {count.total === 0 && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400">
                <Check className="w-3 h-3" />
                Sin alertas
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Filter */}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="px-3 py-1.5 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700"
          >
            <option value="active">Activas</option>
            <option value="acknowledged">Reconocidas</option>
            <option value="resolved">Resueltas</option>
            <option value="all">Todas</option>
          </select>

          {/* Run checks button */}
          <button
            onClick={() => runChecksMutation.mutate()}
            disabled={runChecksMutation.isPending}
            className="btn btn-secondary btn-sm"
          >
            {runChecksMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4" />
            )}
            <span className="hidden sm:inline">Verificar</span>
          </button>
        </div>
      </div>

      {/* Alerts list */}
      {alertsQuery.isLoading ? (
        <div className="card p-8 text-center">
          <Loader2 className="w-8 h-8 mx-auto animate-spin text-slate-400" />
        </div>
      ) : alerts.length === 0 ? (
        <div className="card p-8 text-center">
          <Check className="w-12 h-12 mx-auto text-green-400 mb-4" />
          <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
            {filter === 'active' ? 'Sin alertas activas' : 'No hay alertas'}
          </h3>
          <p className="text-slate-500 dark:text-slate-400">
            {filter === 'active'
              ? 'Todo funciona correctamente'
              : 'No se encontraron alertas con el filtro seleccionado'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {alerts.map((alert: any) => {
            const severity = SEVERITY_CONFIG[alert.severity as keyof typeof SEVERITY_CONFIG] || SEVERITY_CONFIG.warning
            const SeverityIcon = severity.icon
            const TypeIcon = ALERT_TYPE_ICONS[alert.alert_type] || AlertTriangle

            return (
              <div
                key={alert.id}
                className={cn(
                  'card p-4 border-l-4',
                  severity.border,
                  alert.status !== 'active' && 'opacity-60'
                )}
              >
                <div className="flex items-start gap-3">
                  <div className={cn('flex-shrink-0 mt-0.5', severity.color)}>
                    <SeverityIcon className="w-5 h-5" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h4 className="font-medium text-slate-900 dark:text-white">
                          {alert.title}
                        </h4>
                        <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                          {alert.message}
                        </p>
                      </div>

                      <div className="flex items-center gap-1 flex-shrink-0">
                        <TypeIcon className="w-4 h-4 text-slate-400" />
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span>{formatDate(alert.created_at)}</span>
                        {alert.metric_value !== null && (
                          <span className="font-medium">
                            {alert.metric_value > 0 ? '+' : ''}{alert.metric_value}%
                          </span>
                        )}
                        <span className={cn(
                          'px-1.5 py-0.5 rounded',
                          alert.status === 'active' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' :
                          alert.status === 'acknowledged' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' :
                          'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        )}>
                          {alert.status === 'active' ? 'Activa' :
                           alert.status === 'acknowledged' ? 'Reconocida' :
                           alert.status === 'resolved' ? 'Resuelta' : 'Descartada'}
                        </span>
                      </div>

                      {/* Actions */}
                      {alert.status === 'active' && (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => acknowledgeMutation.mutate(alert.id)}
                            disabled={acknowledgeMutation.isPending}
                            className="p-1.5 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                            title="Reconocer"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => resolveMutation.mutate(alert.id)}
                            disabled={resolveMutation.isPending}
                            className="p-1.5 text-slate-400 hover:text-green-600 dark:hover:text-green-400 transition-colors"
                            title="Resolver"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => dismissMutation.mutate(alert.id)}
                            disabled={dismissMutation.isPending}
                            className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            title="Descartar"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
